import React, { useCallback, useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import Container from 'components/Container';
import {
  AdminProgrammeDTO,
  AdminProgrammeRequest,
  createAdminProgramme,
  deactivateAdminProgramme,
  getAdminProgrammes,
  updateAdminProgramme,
} from './DocketAdminService';
import { extractErrorMessage } from '../../../utils/extractErrorMessage';
import { notify } from 'utils/notify';

const DocketProgrammeList = (): JSX.Element => {
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState<AdminProgrammeDTO[]>([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AdminProgrammeRequest>({});
  const [createSaving, setCreateSaving] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<AdminProgrammeDTO | null>(null);
  const [editForm, setEditForm] = useState<AdminProgrammeRequest>({});
  const [editSaving, setEditSaving] = useState(false);

  // Deactivate confirmation
  const [deactivateTarget, setDeactivateTarget] =
    useState<AdminProgrammeDTO | null>(null);
  const [deactivateSaving, setDeactivateSaving] = useState(false);

  const loadProgrammes = useCallback(() => {
    setLoading(true);
    getAdminProgrammes()
      .then((res) => setProgrammes(res.data))
      .catch((err) => notify(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadProgrammes();
  }, [loadProgrammes]);

  function openEdit(p: AdminProgrammeDTO, e: React.MouseEvent) {
    e.stopPropagation();
    setEditTarget(p);
    setEditForm({
      code: p.code,
      displayName: p.displayName,
      description: p.description,
      modeCode: p.modeCode,
      category: p.category,
    });
  }

  async function handleCreate() {
    setCreateSaving(true);
    try {
      await createAdminProgramme(createForm);
      notify('Programme created', 'success');
      setCreateOpen(false);
      setCreateForm({});
      loadProgrammes();
    } catch (err) {
      notify(extractErrorMessage(err));
    } finally {
      setCreateSaving(false);
    }
  }

  async function handleEdit() {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateAdminProgramme(editTarget.id, editForm);
      notify('Programme updated', 'success');
      setEditTarget(null);
      loadProgrammes();
    } catch (err) {
      notify(extractErrorMessage(err));
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeactivate() {
    if (!deactivateTarget) return;
    setDeactivateSaving(true);
    try {
      await deactivateAdminProgramme(deactivateTarget.id);
      notify('Programme deactivated', 'info');
      setDeactivateTarget(null);
      loadProgrammes();
    } catch (err) {
      notify(extractErrorMessage(err));
    } finally {
      setDeactivateSaving(false);
    }
  }

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Typography color="inherit">Admin</Typography>
            <Typography color="text.primary">Docket Programmes</Typography>
          </Breadcrumbs>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Docket Programmes</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Programme
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: 'alternate.main' }}>
                <TableRow>
                  {['Programme', 'Mode', 'Category', 'Enrolments', 'Status', ''].map(
                    (h) => (
                      <TableCell key={h}>
                        <Typography
                          color="text.secondary"
                          variant="caption"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase' }}
                        >
                          {h}
                        </Typography>
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {programmes.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: p.active ? 1 : 0.5 }}
                    onClick={() =>
                      navigate(`/admin/docket/programme/${p.code}`)
                    }
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{p.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.modeCode || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.category || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.enrolmentCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.active ? 'Active' : 'Inactive'}
                        color={p.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" gap={1} alignItems="center">
                        <Button size="small" onClick={(e) => openEdit(p, e)}>
                          Edit
                        </Button>
                        {p.active && (
                          <Button
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeactivateTarget(p);
                            }}
                          >
                            Deactivate
                          </Button>
                        )}
                        <Typography
                          color="primary"
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ ml: 1 }}
                          onClick={() =>
                            navigate(`/admin/docket/programme/${p.code}`)
                          }
                        >
                          Manage →
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {programmes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        color="text.secondary"
                        textAlign="center"
                        py={4}
                      >
                        No programmes found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create Programme Dialog */}
        <Dialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>New Programme</DialogTitle>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
          >
            <TextField
              label="Display Name"
              fullWidth
              value={createForm.displayName ?? ''}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, displayName: e.target.value }))
              }
              autoFocus
            />
            <TextField
              label="Code"
              fullWidth
              value={createForm.code ?? ''}
              onChange={(e) =>
                setCreateForm((f) => ({
                  ...f,
                  code: e.target.value.toUpperCase().replace(/\s+/g, '_'),
                }))
              }
              helperText="Unique identifier — uppercase, underscores only"
            />
            <TextField
              label="Mode Code"
              fullWidth
              value={createForm.modeCode ?? ''}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, modeCode: e.target.value }))
              }
              placeholder="e.g. FINANCE, HEALTH, LEGAL"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={createForm.description ?? ''}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCreateOpen(false);
                setCreateForm({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={
                createSaving ||
                !createForm.displayName?.trim() ||
                !createForm.code?.trim()
              }
            >
              {createSaving ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Programme Dialog */}
        <Dialog
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Programme</DialogTitle>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
          >
            <TextField
              label="Display Name"
              fullWidth
              value={editForm.displayName ?? ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, displayName: e.target.value }))
              }
              autoFocus
            />
            <TextField
              label="Code"
              fullWidth
              value={editForm.code ?? ''}
              onChange={(e) =>
                setEditForm((f) => ({
                  ...f,
                  code: e.target.value.toUpperCase().replace(/\s+/g, '_'),
                }))
              }
              helperText="Unique identifier — uppercase, underscores only"
            />
            <TextField
              label="Mode Code"
              fullWidth
              value={editForm.modeCode ?? ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, modeCode: e.target.value }))
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editForm.description ?? ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEdit}
              disabled={editSaving}
            >
              {editSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Deactivate Confirmation Dialog */}
        <Dialog
          open={!!deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Deactivate Programme</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to deactivate{' '}
              <strong>{deactivateTarget?.displayName}</strong>? Existing
              enrolments will not be affected.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleDeactivate}
              disabled={deactivateSaving}
            >
              {deactivateSaving ? 'Deactivating…' : 'Deactivate'}
            </Button>
          </DialogActions>
        </Dialog>
      </AdminDashboard>
    </Container>
  );
};

export default DocketProgrammeList;
